import React, { useCallback } from 'react';
import { Box, Typography, Card, CardContent, IconButton, Alert, LinearProgress, Button } from '@mui/material';
import { Close, CloudUpload } from '@mui/icons-material';
import { useVideoUpload } from '@/pages/Record/Recorder/hooks';

interface VideoFile {
  file: File;
  url: string;
  duration: number;
  name: string;
}

interface VideoPreviewProps {
  video: VideoFile;
  planLimit: number;
  onClear: () => void;
  onUploadComplete?: (result: { success: boolean; noteId?: string; filePath?: string; error?: string }) => void;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({ video, planLimit, onClear, onUploadComplete }) => {
  const uploader = useVideoUpload({
    onUploadComplete,
    redirectOnSuccess: true,
    redirectDelay: 1500,
  });

  const isWithinLimit = video.duration <= planLimit;

  const formatTime = useCallback((seconds: number): string => {
    if (!isFinite(seconds) || seconds < 0) return '00:00';
    const date = new Date(seconds * 1000);
    try {
      return date.toISOString().substring(14, 19);
    } catch {
      return '00:00';
    }
  }, []);

  const handleUploadVideo = async () => {
    if (!isWithinLimit) return;
    await uploader.uploadVideo(video.file);
  };

  return (
    <Card
      sx={{
        mb: 3,
        maxWidth: '800px',
        mx: 'auto',
        boxShadow: 2,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {video.name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Duration: {formatTime(video.duration)}
            </Typography>
          </Box>
          <IconButton onClick={onClear} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Video Player */}
        <Box
          sx={{
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: 'black',
            maxWidth: '600px',
            mx: 'auto',
            aspectRatio: '16/9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <video
            src={video.url}
            controls
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Plan Status */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'primary.main',
                color: 'primary.main',
                fontSize: '0.8125rem',
              }}
            >
              Plan Limit: {formatTime(planLimit)}
            </Box>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: isWithinLimit ? 'success.main' : 'error.main',
                color: 'white',
                fontSize: '0.8125rem',
              }}
            >
              Video: {formatTime(video.duration)}
            </Box>
          </Box>
        </Box>

        {/* Warning if video exceeds plan limit */}
        {!isWithinLimit && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            This video exceeds your plan limit of {formatTime(planLimit)}. Please upload a shorter video.
          </Alert>
        )}

        {/* Upload Progress */}
        {uploader.isUploading && uploader.uploadProgress && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Please don&apos;t close this page while the upload is in progress.
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploader.uploadProgress.progress}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                {uploader.uploadProgress.progress}% complete
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Error Display */}
        {uploader.uploadError && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="error" onClose={uploader.clearUploadError}>
              <Typography variant="body2">Upload failed: {uploader.uploadError}</Typography>
            </Alert>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            disabled={!isWithinLimit || uploader.isUploading}
            onClick={handleUploadVideo}
            startIcon={<CloudUpload />}
            sx={{ minWidth: 200 }}
          >
            {uploader.isUploading
              ? 'Uploading...'
              : isWithinLimit
                ? `Upload ${formatTime(video.duration)} Video`
                : 'Video exceeds plan limit'}
          </Button>
        </Box>

        {/* Info Text */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Video will be uploaded and processed on the server.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default VideoPreview;
